import React, { useState } from 'react';
import { connect } from 'react-redux';
import { debounce, get as objectGet } from 'lodash';

import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import AsyncSelect from 'react-select/async';
import { components } from 'react-select';

import { removeLocation } from 'bundles/connect/components/store/actions/criteria';
import classNames from 'classnames';

import type { MultiValueProps, MenuListProps, ControlProps, InputProps, GroupBase } from 'react-select';
import type { Criteria } from 'bundles/connect/types';

type MultiValueExtraProps = { index: number; getValue: () => any[] };
const MultiValue = (
  props: MultiValueProps<any, boolean, GroupBase<any>> & MultiValueExtraProps
) => {
  const { index, getValue } = props;
  if (index === 0 || props.selectProps.menuIsOpen) {
    return <components.MultiValue {...props} />;
  } else if (index === 1) {
    return (
      <div
        style={{
          backgroundColor: 'hsl(0, 0%, 90%)',
          margin: '2px',
          borderRadius: '2px',
          color: 'hsl(0, 0%, 20%)',
          fontSize: '85%',
          padding: '3px 6px'
        }}
      >+ {getValue().length - 1} more</div>
    );
  } else {
    return null;
  }
};

const MenuList = (props: MenuListProps<any, boolean, GroupBase<any>>) => {
  const { children } = props;
  return (
    <components.MenuList {...props}>
      {children}
      {Array.isArray(children) && children.length > 0 && (
        <div className="small text-end px-1 py-2">
          powered by
          <img className="mx-1" src="/images/google_integration/google_on_white.png" />
        </div>
      )}
    </components.MenuList>
  );
};

const Control = (props: ControlProps<any, boolean, GroupBase<any>>) => {
  const { children } = props;
  return (
    <components.Control {...props}>
      <FontAwesomeIcon
        className="icon text-primary clickable align-self-start"
        icon={faMagnifyingGlass}
        style={{ marginTop: '11px' }}
      />
      {children}
    </components.Control>
  );
};

// Input on it's own line
const Input = (props: InputProps<any, true, GroupBase<any>>) => {
  const focused = props.selectProps.menuIsOpen;
  const anySelectedValues = props.getValue().length > 0;

  if (props.isHidden || !anySelectedValues || !focused) return (
    <components.Input {...props} />
  );

  return (
    <div className="w-100">
      <components.Input {...props} />
    </div>
  );
};

type LocationInputProps = {
  hasPolygonSearch: boolean;
  locations: Criteria['locationsAttributes'];
  removeLocation: (id: string) => void;
}

export function LocationInput({ hasPolygonSearch, locations, removeLocation }: LocationInputProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [sessionToken, setSessionToken] = useState<(null | google.maps.places.AutocompleteSessionToken)>(null);

  const userLocations = (locations || []).map(
    loc => ({ label: loc.name || 'Map Drawing', ...loc })
  );

  const handleOnFocus = () => {
    const sessionToken = new google.maps.places.AutocompleteSessionToken();
    setSessionToken(sessionToken);
  };

  const placeSelected = (places, event) => {
    if (event.action === 'clear') {
      clearLocation();
    } else if (event.action === 'select-option' && places.length > 0) {
      const place = places.pop();
      if (!place) return;

      const hasCountyType = place.types && place.types.includes('administrative_area_level_2');
      const hasCountyName = place.label.toLowerCase().includes('county') ||
                            place.label.toLowerCase().includes('parish'); // Handle Louisiana

      const isCounty = hasCountyType || hasCountyName;
      const event = new CustomEvent('placeSelect', { detail:
        { place: place.placePrediction, address: place.label, isCounty } });
      window.dispatchEvent(event);
    } else if (event.action === 'remove-value' && event.removedValue) {
      removeLocation(event.removedValue.id);
    }
  };

  const fetchOptions = async (input: string) => {
    const placesLib: any = await google.maps.importLibrary('places');
    const AutocompleteSuggestion = placesLib.AutocompleteSuggestion;
    const request = {
      includedRegionCodes: ['US'],
      input: input,
      language: 'en-US',
      region: 'US',
      sessionToken: sessionToken
    };

    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
    return suggestions;
  };

  const loadOptions = (inputValue, callback) => {
    fetchOptions(inputValue).then((predictions) => {
      const response = predictions.map(prediction => (
        {
          id: prediction.placePrediction.placeId,
          value: prediction.placePrediction.placeId,
          label: prediction.placePrediction.text.text,
          types: prediction.placePrediction.types,
          placePrediction: prediction.placePrediction
        }
      ));
      callback(response);
    }).catch((e) => {
      if (e == google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
        callback([]);
      }
    });
  };

  const debouncedLoadOptions = debounce(loadOptions, 300);


  const clearLocation = () => {
    const event = new CustomEvent('clearLocation');
    window.dispatchEvent(event);
  };

  return (
    <div className="position-relative">
      <AsyncSelect
        className={classNames('rounded', { 'floating-input': menuOpen })}
        classNames={{
          control: (state: any) => (state.isFocused || hasPolygonSearch)
            ? 'ps-3 rounded border-primary-focus'
            : 'ps-3 rounded border',
        }}
        components={{
          Control,
          DropdownIndicator: null,
          MenuList,
          MultiValue,
          Input
        }}
        defaultValue={locations}
        isClearable
        isMulti
        key="location-search"
        loadOptions={debouncedLoadOptions}
        noOptionsMessage={({ inputValue }) => !inputValue ? null : 'No locations found'}
        placeholder="Search Location"
        styles={{
          control: base => ({
            ...base,
            boxShadow: 'none', // no box-shadow
            minHeight: '36px',
            cursor: 'text'
          }),
          multiValue: base => ({
            ...base,
            minWidth: 50
          })
        }}
        value={userLocations}
        onChange={placeSelected}
        onFocus={handleOnFocus}
        onMenuClose={() => setMenuOpen(false)}
        onMenuOpen={() => setMenuOpen(true)}
      />
    </div>
  );
}

const mapStateToProps = ({ criteria }) => {
  const locations = objectGet(criteria, 'locationsAttributes') || [];

  return { hasPolygonSearch: locations.length > 0, locations };
};

export default connect(mapStateToProps, { removeLocation })(LocationInput);
